const RECOVERABLE_NETWORK_ERROR_PATTERNS = [
  'fetch failed',
  'failed to fetch',
  'networkerror',
  'network error',
  'network request failed',
  'load failed',
  'econnrefused',
  'econnreset',
  'enotfound',
  'enetunreach',
  'eai_again',
  'etimedout',
  'timed out',
  'timeout',
  'socket hang up',
  'connection closed',
  'connection terminated',
  'body timeout',
  'connect timeout',
];

const DEFAULT_NETWORK_PROBE_TIMEOUT_MS = 2500;
const NETWORK_PROBE_PATH = '/favicon.ico';

type NetworkProbeFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

function collectErrorMessages(
  error: unknown,
  seen = new Set<unknown>()
): string[] {
  if (error === null || error === undefined || seen.has(error)) {
    return [];
  }

  seen.add(error);

  if (typeof error === 'string') {
    return [error];
  }

  if (error instanceof Error) {
    const messages = [error.name, error.message];
    const cause = error.cause;

    return cause === undefined
      ? messages
      : [...messages, ...collectErrorMessages(cause, seen)];
  }

  if (typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' ? [message] : [];
  }

  return [String(error)];
}

export function isRecoverableNetworkError(error: unknown): boolean {
  const message = collectErrorMessages(error).join(' ').toLowerCase();

  return RECOVERABLE_NETWORK_ERROR_PATTERNS.some((pattern) =>
    message.includes(pattern)
  );
}

export function getBrowserOnlineStatus(): boolean {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.onLine !== 'boolean'
  ) {
    return true;
  }

  return navigator.onLine;
}

export function getNetworkStatusProbeUrl(now = Date.now()): string {
  const probePath = `${NETWORK_PROBE_PATH}?network-status=${now}`;

  if (typeof window === 'undefined' || !window.location?.origin) {
    return probePath;
  }

  return new URL(probePath, window.location.origin).toString();
}

export async function canReachAppOrigin({
  fetchImpl = globalThis.fetch?.bind(globalThis) as
    | NetworkProbeFetch
    | undefined,
  now = Date.now,
  timeoutMs = DEFAULT_NETWORK_PROBE_TIMEOUT_MS,
}: {
  fetchImpl?: NetworkProbeFetch;
  now?: () => number;
  timeoutMs?: number;
} = {}): Promise<boolean> {
  if (typeof fetchImpl !== 'function') {
    return false;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetchImpl(getNetworkStatusProbeUrl(now()), {
      cache: 'no-store',
      credentials: 'same-origin',
      method: 'HEAD',
      mode: 'same-origin',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}
